/**
 * Files API service
 * Handles all file-related API calls
 */

import api from './api'

const filesService = {
  /**
   * List files in a project
   * @param {string} projectId - UUID of the project
   * @param {boolean} includeDeleted - Include soft-deleted files
   * @returns {Promise} List of files with pagination
   */
  listFiles: async (projectId, includeDeleted = false) => {
    const response = await api.get(
      `/files/?project=${projectId}${includeDeleted ? '&include_deleted=true' : ''}`
    )
    return response.data
  },

  /**
   * Upload a file to a project
   * @param {string} projectId - UUID of the project
   * @param {File} file - File object to upload
   * @param {string} category - Category: 'uploaded' or 'generated'
   * @returns {Promise} Created file metadata
   */
  uploadFile: async (projectId, file, category = 'uploaded') => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('project_id', projectId)
    formData.append('category', category)

    // Remove default Content-Type header and let browser set multipart/form-data with boundary
    const response = await api.post('/files/', formData, {
      headers: {
        'Content-Type': undefined,
      },
    })
    return response.data
  },

  /**
   * Get file metadata and status
   * @param {string} fileId - UUID of the file
   * @returns {Promise} File metadata
   */
  getFile: async (fileId) => {
    const response = await api.get(`/files/${fileId}/`)
    return response.data
  },

  /**
   * Download a file
   * @param {string} fileId - UUID of the file
   * @returns {Promise} Blob data
   */
  downloadFile: async (fileId) => {
    const response = await api.get(`/files/${fileId}/download/`, {
      responseType: 'blob',
    })
    return response
  },

  /**
   * Soft delete a file
   * @param {string} fileId - UUID of the file
   * @returns {Promise}
   */
  deleteFile: async (fileId) => {
    const response = await api.delete(`/files/${fileId}/`)
    return response.data
  },

  /**
   * Restore a soft-deleted file
   * @param {string} fileId - UUID of the file
   * @returns {Promise} Restored file data
   */
  restoreFile: async (fileId) => {
    const response = await api.post(`/files/${fileId}/restore/`)
    return response.data
  },

  /**
   * Poll file status until processing completes
   * @param {string} fileId - UUID of the file
   * @param {Function} onUpdate - Callback for status updates
   * @param {number} interval - Polling interval in milliseconds
   * @returns {Promise} Final file data
   */
  pollFileStatus: async (fileId, onUpdate, interval = 2000) => {
    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          const file = await filesService.getFile(fileId)

          // Call update callback
          if (onUpdate) {
            onUpdate(file)
          }

          if (file.status === 'completed') {
            resolve(file)
          } else if (file.status === 'failed') {
            reject(new Error(file.error_message || 'File processing failed'))
          } else {
            // Continue polling
            setTimeout(poll, interval)
          }
        } catch (error) {
          reject(error)
        }
      }

      poll()
    })
  },
}

export default filesService
